import { useRef, useCallback, useEffect } from 'react';
import { fitCanvasToSource } from '@/lib/media/fitCanvasToSource';

interface ExtractorOptions {
  canvasWidth?: number;
  canvasHeight?: number;
  quality?: number;
  maxCachedFrames?: number;
  /** Cache/seek precision; frame inspection uses the editor frame rate. */
  sampleFps?: number;
  /** Size the canvas to the source video's aspect (long side capped at
   *  max(canvasWidth, canvasHeight), never upscaled) instead of center-
   *  cropping the source into the fixed canvas box. For full-frame captures
   *  (e.g. the clip cover) where a portrait source must stay portrait. */
  fitToSource?: boolean;
}

const DEFAULT_CANVAS_WIDTH = 64;
const DEFAULT_CANVAS_HEIGHT = 36;
const DEFAULT_JPEG_QUALITY = 0.6;

const CORS_PROXY_URL = 'https://cors.autoclipper.live';

// Route a URL through the cors-anywhere proxy using host:port/path format (the
// same shape used by video-metadata.ts). Avoids the double-slash collapsing
// that breaks https://host URLs through Traefik/Nginx reverse proxies.
function buildProxiedUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const hostWithPort = parsed.port
      ? parsed.host
      : `${parsed.hostname}:${parsed.protocol === 'https:' ? '443' : '80'}`;
    return `${CORS_PROXY_URL}/${hostWithPort}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

/**
 * Hook that manages a single shared <video> element to extract frames sequentially.
 * Frames are cached by rounded time to avoid redundant work.
 * Resolution/quality can be tuned per call site (scene thumbnails stay low-res;
 * editor previews can request larger frames).
 */
export function useVideoFrameExtractor(
  videoUrl: string | undefined,
  options?: ExtractorOptions,
) {
  const CANVAS_WIDTH = options?.canvasWidth ?? DEFAULT_CANVAS_WIDTH;
  const CANVAS_HEIGHT = options?.canvasHeight ?? DEFAULT_CANVAS_HEIGHT;
  const JPEG_QUALITY = options?.quality ?? DEFAULT_JPEG_QUALITY;
  const MAX_CACHED_FRAMES = options?.maxCachedFrames ?? Infinity;
  const SAMPLE_FPS = options?.sampleFps ?? 10;
  const FIT_TO_SOURCE = options?.fitToSource ?? false;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cacheRef = useRef<Map<number, string>>(new Map());
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const cleanedUpRef = useRef(false);
  // Once a direct load fails (no CORS headers on the origin server), we flip
  // this for the lifetime of the hook instance so subsequent seeks go straight
  // through the proxy instead of retrying the direct URL each time.
  const useProxyRef = useRef(false);

  // Reset cache when URL changes
  useEffect(() => {
    cacheRef.current.clear();
    cleanedUpRef.current = false;
    useProxyRef.current = false;
    return () => {
      cleanedUpRef.current = true;
      if (videoRef.current) {
        videoRef.current.src = '';
        videoRef.current.load();
        videoRef.current = null;
      }
      cacheRef.current.clear();
    };
  }, [videoUrl]);

  const loadVideoElement = useCallback(
    (src: string): Promise<HTMLVideoElement> => {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = src;
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        const onLoaded = () => {
          if (cleanedUpRef.current) {
            video.src = '';
            video.load();
            return;
          }
          resolve(video);
        };
        const onError = () => {
          video.src = '';
          video.load();
          reject(new Error('video load error'));
        };
        video.addEventListener('loadedmetadata', onLoaded, { once: true });
        video.addEventListener('error', onError, { once: true });
      });
    },
    [],
  );

  const getVideo = useCallback(async (): Promise<HTMLVideoElement> => {
    if (videoRef.current) return videoRef.current;
    if (!videoUrl) throw new Error('no video url');

    // Fast path: if we've already proven the direct URL fails, go straight
    // to the proxy. Otherwise try direct first and fall back on failure.
    if (useProxyRef.current) {
      const video = await loadVideoElement(buildProxiedUrl(videoUrl));
      videoRef.current = video;
      return video;
    }

    try {
      const video = await loadVideoElement(videoUrl);
      videoRef.current = video;
      return video;
    } catch {
      if (cleanedUpRef.current) throw new Error('cleaned up');
      useProxyRef.current = true;
      const video = await loadVideoElement(buildProxiedUrl(videoUrl));
      videoRef.current = video;
      return video;
    }
  }, [videoUrl, loadVideoElement]);

  const seekAndDraw = useCallback(
    async (roundedTime: number): Promise<string | null> => {
      const video = await getVideo();
      if (cleanedUpRef.current) return null;

      const safeTime = Math.max(0, Math.min(roundedTime, Math.max(0, (video.duration || 0) - 0.01)));
      if (!Number.isFinite(safeTime)) return null;

      // Seek
      await new Promise<void>((resolve, reject) => {
        video.currentTime = safeTime;
        video.addEventListener('seeked', () => resolve(), { once: true });
        video.addEventListener('error', () => reject(new Error('seek error')), { once: true });
      });

      if (cleanedUpRef.current) return null;

      // Draw to offscreen canvas
      const fitted = FIT_TO_SOURCE
        ? fitCanvasToSource({
            sourceWidth: video.videoWidth,
            sourceHeight: video.videoHeight,
            maxLongSide: Math.max(CANVAS_WIDTH, CANVAS_HEIGHT),
          })
        : null;
      const canvasWidth = fitted?.width ?? CANVAS_WIDTH;
      const canvasHeight = fitted?.height ?? CANVAS_HEIGHT;
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const vw = video.videoWidth || canvasWidth;
      const vh = video.videoHeight || canvasHeight;
      const canvasAspect = canvasWidth / canvasHeight;
      const videoAspect = vw / vh;

      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (videoAspect > canvasAspect) {
        const nw = vh * canvasAspect;
        sx = (vw - nw) / 2;
        sw = nw;
      } else if (videoAspect < canvasAspect) {
        const nh = vw / canvasAspect;
        sy = (vh - nh) / 2;
        sh = nh;
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);
      // toDataURL throws SecurityError if the video tainted the canvas
      // (CORS response missing/invalid). Let the caller decide whether to
      // retry through the proxy.
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      cacheRef.current.set(roundedTime, dataUrl);
      while (cacheRef.current.size > MAX_CACHED_FRAMES) cacheRef.current.delete(cacheRef.current.keys().next().value!);
      return dataUrl;
    },
    [getVideo, CANVAS_WIDTH, CANVAS_HEIGHT, JPEG_QUALITY, MAX_CACHED_FRAMES, FIT_TO_SOURCE],
  );

  const extractFrame = useCallback(
    (time: number): Promise<string | null> => {
      const roundedTime = Math.round(time * SAMPLE_FPS) / SAMPLE_FPS;
      const cached = cacheRef.current.get(roundedTime);
      if (cached) return Promise.resolve(cached);

      // Chain onto the sequential queue
      const promise = queueRef.current
        .then(async () => {
          // Check cache again in case it was filled while queued
          const cached2 = cacheRef.current.get(roundedTime);
          if (cached2) return cached2;
          if (cleanedUpRef.current) return null;

          try {
            return await seekAndDraw(roundedTime);
          } catch (err) {
            // If the canvas was tainted (video loaded without proper CORS
            // headers even though the element didn't error), tear down and
            // retry once through the proxy.
            if (
              !useProxyRef.current &&
              err instanceof Error &&
              (err.name === 'SecurityError' || /tainted/i.test(err.message))
            ) {
              useProxyRef.current = true;
              if (videoRef.current) {
                videoRef.current.src = '';
                videoRef.current.load();
                videoRef.current = null;
              }
              if (cleanedUpRef.current) return null;
              return await seekAndDraw(roundedTime);
            }
            return null;
          }
        })
        .catch(() => null);

      // Update queue so next call waits for this one
      queueRef.current = promise.then(() => {});
      return promise;
    },
    [seekAndDraw, SAMPLE_FPS],
  );

  return { extractFrame };
}
